import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { IconGlobe } from '~/ui/core/icons';

// Deep-import (not via the chat barrel): the chat barrel pulls ChatBody, whose
// block renderers import job-architecture-v2 modules — a module-eval cycle.
import { useChatComposerCallbacks } from '../../../chat/chat-composer-contexts';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { usePublishAction } from '../../hooks/use-publish-action';
import { PromptShell } from './PromptShell';
import { PromptVersionLabel } from './PromptVersionLabel';
import { fireTastefulConfettiAndSettle } from './TastefulConfetti';

type ItemType = 'job-level' | 'job-role';

type PublishPromptProps = {
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: ItemType;
    readonly title: string;
    readonly jobType: string;
    readonly isNewVersion: boolean;
    readonly previousVersion?: number;
    readonly currentVersion?: number;
};

export const PublishPrompt: FunctionComponent<PublishPromptProps> = ({
    architectureId,
    item,
    itemType,
    title,
    jobType,
    isNewVersion,
    previousVersion,
    currentVersion,
}) => {
    const translate = useTranslate();
    const { sendMessage } = useChatComposerCallbacks();
    const { handlePublish } = usePublishAction({ architectureId, item, itemType });

    const handlePublishClick = useCallback(
        async (event: { readonly clientX: number; readonly clientY: number }): Promise<void> => {
            await fireTastefulConfettiAndSettle(event);
            await handlePublish();
        },
        [handlePublish],
    );

    const handleCompare = useCallback((): void => {
        sendMessage(translate('pages.job-architecture-v2.prompts.compare'));
    }, [sendMessage, translate]);

    const safeTitle = title.trim().length > 0 ? title : translate('pages.job-architecture-v2.prompts.fallback-title');
    const question = isNewVersion
        ? translate('pages.job-architecture-v2.prompts.publish-question-new-version', { jobType })
        : translate('pages.job-architecture-v2.prompts.publish-question', { title: safeTitle });

    const versionLabel =
        isNewVersion && is.number(previousVersion) && is.number(currentVersion) ? (
            <PromptVersionLabel currentVersion={currentVersion} previousVersion={previousVersion} />
        ) : undefined;

    return (
        <PromptShell
            question={question}
            versionLabel={versionLabel}
            actions={
                <>
                    {isNewVersion && (
                        <Badge variant="gray" onClick={handleCompare}>
                            {translate('pages.job-architecture-v2.prompts.compare')}
                        </Badge>
                    )}
                    <Badge variant="success" onClick={handlePublishClick}>
                        <IconGlobe size={11} />
                        {translate('pages.job-architecture-v2.job-role-publish-button')}
                    </Badge>
                </>
            }
        />
    );
};
